import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Star, Quote, Loader2, ImagePlus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function StarRating({ value, onChange, size = "w-6 h-6", readOnly = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onChange?.(n)}
            data-testid={readOnly ? undefined : `star-${n}`}
            className={`${readOnly ? "cursor-default" : "cursor-pointer hover:scale-110 transition-transform"}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            <Star
              className={`${size} ${
                filled ? "fill-secondary text-secondary" : "text-gray-300"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export function Reviews() {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState(null); // data URL
  const [submitting, setSubmitting] = useState(false);

  // Compress + convert selected image to base64 (max 900px, JPEG q0.8)
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 900;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        if (dataUrl.length > 1_400_000) {
          toast.error("Photo is too large — pick a smaller one");
          return;
        }
        setPhoto(dataUrl);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/reviews");
      setData(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (user && !name) setName(user.name || "");
  }, [user, name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Please pick a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        name: name.trim(),
        location: location.trim() || null,
        rating,
        comment: comment.trim(),
        photo: photo || null,
      });
      toast.success("Thanks! Your review is pending admin approval and will appear once reviewed.");
      setShowForm(false);
      setComment("");
      setRating(0);
      setLocation("");
      setPhoto(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-20 bg-primary-foreground relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl translate-y-1/2 translate-x-1/2" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge variant="outline" className="mb-4 border-primary/20 text-primary px-4 py-1">
            Traveler Stories
          </Badge>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6">
            Reviews & <span className="text-secondary">Comments</span>
          </h2>
          {data.count > 0 && (
            <div className="flex items-center justify-center gap-3" data-testid="reviews-summary">
              <StarRating value={Math.round(data.average)} readOnly size="w-6 h-6" />
              <span className="text-2xl font-serif font-bold text-primary">{data.average}</span>
              <span className="text-muted-foreground">
                ({data.count} review{data.count === 1 ? "" : "s"})
              </span>
            </div>
          )}
        </div>

        <div className="text-center mb-10">
          <Button
            onClick={() => setShowForm((s) => !s)}
            data-testid="leave-review-btn"
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 h-12"
          >
            <Star className="w-4 h-4 mr-2" />
            {showForm ? "Close" : "Leave a Review"}
          </Button>
        </div>

        {showForm && (
          <Card className="max-w-2xl mx-auto mb-12 border-primary/10 shadow-xl bg-white">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="block mb-2">Your rating</Label>
                  <StarRating value={rating} onChange={setRating} size="w-9 h-9" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rev-name">Name</Label>
                    <Input
                      id="rev-name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      data-testid="review-name-input"
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rev-loc">Country / City (optional)</Label>
                    <Input
                      id="rev-loc"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="e.g. London, UK"
                      data-testid="review-location-input"
                      className="bg-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rev-comment">Your comment</Label>
                  <Textarea
                    id="rev-comment"
                    required
                    minLength={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Tell other travelers about your experience…"
                    data-testid="review-comment-input"
                    className="bg-white min-h-[120px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Add a photo (optional)</Label>
                  {photo ? (
                    <div className="relative inline-block">
                      <img
                        src={photo}
                        alt="review preview"
                        className="w-32 h-32 object-cover rounded-lg border border-primary/10"
                      />
                      <button
                        type="button"
                        onClick={() => setPhoto(null)}
                        data-testid="review-photo-remove"
                        className="absolute -top-2 -right-2 bg-white border border-primary/20 rounded-full p-1 hover:bg-red-50"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="rev-photo"
                      data-testid="review-photo-upload"
                      className="inline-flex items-center gap-2 cursor-pointer border border-primary/20 rounded-full px-4 py-2 text-sm text-primary hover:bg-primary/5 transition-colors"
                    >
                      <ImagePlus className="w-4 h-4" />
                      Upload photo
                    </label>
                  )}
                  <input
                    id="rev-photo"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="review-submit-btn"
                  className="bg-primary text-white hover:bg-primary/90 rounded-full"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post review"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : data.reviews.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">
            No reviews yet — be the first to share your experience!
          </p>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="reviews-list"
          >
            {data.reviews.map((r) => (
              <Card
                key={r.review_id}
                className="border-primary/10 hover:shadow-lg transition-shadow bg-white"
              >
                <CardContent className="p-6">
                  <Quote className="w-8 h-8 text-secondary/30 mb-3" />
                  <StarRating value={r.rating} readOnly size="w-4 h-4" />
                  <p className="text-foreground mt-4 mb-4 line-clamp-5">{r.comment}</p>
                  {r.photo && (
                    <img
                      src={r.photo}
                      alt="review"
                      className="w-full h-48 object-cover rounded-lg mb-4 border border-primary/10"
                    />
                  )}
                  <div className="flex items-center gap-3 pt-4 border-t border-primary/5">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={r.picture} alt={r.name} />
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {(r.name || "?").charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-serif font-bold text-primary truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {r.location || new Date(r.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
